import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft,
    CheckCircle2,
    Clock,
    Search,
    UserCheck,
    Users,
    XCircle
} from 'lucide-react';

export const GroupPage = () => {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const { getGroup, confirmStudent, unconfirmStudent, isAuthenticated, userRole } = useApp();
    const [searchQuery, setSearchQuery] = useState('');

    const group = getGroup(groupId);

    if (!group) return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-slate-400">
            <h2 className="text-2xl font-bold mb-4">Группа не найдена</h2>
            <button onClick={() => navigate(-1)} className="text-indigo-400 flex items-center gap-2">
                <ArrowLeft size={20} /> Вернуться назад
            </button>
        </div>
    );

    const confirmedCount = group.students.filter(s => s.confirmed).length;
    const totalCount = group.students.length;
    const progress = Math.round((confirmedCount / totalCount) * 100);

    const filteredStudents = group.students.filter(s =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-6xl mx-auto pb-20"
        >
            {/* Header & Back Button */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div className="flex items-center gap-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-3 bg-slate-900 border border-white/5 rounded-2xl text-slate-400 hover:text-white hover:bg-slate-800 transition-all group"
                    >
                        <ArrowLeft size={24} className="group-hover:-translate-x-1 transition-transform" />
                    </button>
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h1 className="text-4xl font-black text-white tracking-tight">{group.name}</h1>
                            <span className="px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-xs font-bold uppercase">
                                {group.studyType}
                            </span>
                        </div>
                        <p className="text-slate-500 flex items-center gap-2">
                            <Users size={16} /> Список студентов и отметки о присутствии
                        </p>
                    </div>
                </div>

                {/* Групповая статистика */}
                <div className="bg-slate-900/50 backdrop-blur-xl border border-white/5 rounded-[24px] p-5 flex items-center gap-6">
                    <div className="relative w-16 h-16 flex items-center justify-center">
                        <svg className="w-full h-full transform -rotate-90">
                            <circle cx="32" cy="32" r="28" fill="transparent" stroke="currentColor" strokeWidth="4" className="text-white/5" />
                            <motion.circle
                                cx="32" cy="32" r="28" fill="transparent" stroke="currentColor" strokeWidth="4"
                                className="text-indigo-500"
                                strokeDasharray={176}
                                initial={{ strokeDashoffset: 176 }}
                                animate={{ strokeDashoffset: 176 - (176 * progress) / 100 }}
                                transition={{ duration: 1, ease: "easeOut" }}
                            />
                        </svg>
                        <span className="absolute text-xs font-bold text-white">{progress}%</span>
                    </div>
                    <div>
                        <div className="text-2xl font-black text-white">{confirmedCount} / {totalCount}</div>
                        <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Подтверждено</div>
                    </div>
                </div>
            </div>

            {/* Фильтр и Поиск */}
            <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                <input
                    type="text"
                    placeholder="Поиск студента по фамилии..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-slate-900/50 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all"
                />
            </div>

            {/* Список студентов */}
            <div className="bg-slate-900/30 backdrop-blur-md border border-white/5 rounded-[32px] overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-white/5 bg-white/[0.02]">
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-500">№</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-500">Студент</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-500">Статус</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-500 text-right">Действие</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            <AnimatePresence mode='popLayout'>
                                {filteredStudents.map((student, idx) => (
                                    <motion.tr
                                        key={student.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        layout
                                        className="group hover:bg-white/[0.02] transition-colors"
                                    >
                                        <td className="p-6 text-slate-600 font-mono text-sm">{idx + 1}</td>
                                        <td className="p-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-sm font-bold text-slate-300 border border-white/10 group-hover:border-indigo-500/50 transition-colors">
                                                    {student.name.charAt(0)}
                                                </div>
                                                <span className="font-bold text-slate-200 group-hover:text-white transition-colors">
                                                    {student.name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-6">
                                            {student.confirmed ? (
                                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold">
                                                    <CheckCircle2 size={14} /> Подтвержден
                                                </div>
                                            ) : (
                                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold">
                                                    <Clock size={14} /> Ожидает
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-6 text-right">
                                            {!student.confirmed && isAuthenticated && userRole !== 'read_only' && (
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() => confirmStudent(student.id)}
                                                    className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2 rounded-xl text-sm font-bold transition-all shadow-lg shadow-indigo-600/20"
                                                >
                                                    <UserCheck size={16} /> Подтвердить
                                                </motion.button>
                                            )}
                                            {student.confirmed && isAuthenticated && userRole !== 'read_only' && (
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() => unconfirmStudent(student.id)}
                                                    className="inline-flex items-center gap-2 bg-red-600/80 hover:bg-red-600 text-white px-5 py-2 rounded-xl text-sm font-bold transition-all shadow-lg shadow-red-600/20"
                                                >
                                                    <XCircle size={16} /> Отменить
                                                </motion.button>
                                            )}
                                            {student.confirmed && (!isAuthenticated || userRole === 'read_only') && (
                                                <div className="text-emerald-400 flex items-center justify-end gap-1 text-sm font-medium">
                                                    <CheckCircle2 size={16} /> Подтвержден
                                                </div>
                                            )}
                                        </td>
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                        </tbody>
                    </table>
                </div>

                {filteredStudents.length === 0 && (
                    <div className="p-20 text-center text-slate-500">
                        <Search size={48} className="mx-auto mb-4 opacity-20" />
                        <p className="text-lg">Студенты не найдены</p>
                        <p className="text-sm">Попробуйте изменить запрос</p>
                    </div>
                )}
            </div>
        </motion.div>
    );
};